import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, PackageCheck, ShoppingBag, Truck } from "lucide-react";
import { api } from "@/lib/api";
import TriskelionMark from "@/components/TriskelionMark";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import Footer from "@/components/Footer";
import { T } from "@/constants/testIds";

const HERO_IMG =
  "https://images.unsplash.com/photo-1548123378-bde4eca81d2d?crop=entropy&cs=srgb&fm=jpg&q=85";
const HERITAGE_IMG =
  "https://images.unsplash.com/photo-1789151537257-8032c76c00ab?crop=entropy&cs=srgb&fm=jpg&q=85";

function HeroLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

const STEPS = [
  {
    icon: ShoppingBag,
    title: "You order",
    body: "Pick your piece, choose your size, pay securely by card. Every product shows its own estimated delivery time.",
  },
  {
    icon: PackageCheck,
    title: "We check & ship",
    body: "Each piece is sourced to order from our manufacturing partners, quality-checked by us, then posted to you.",
  },
  {
    icon: Truck,
    title: "It's delivered",
    body: "Tracked delivery to your door across the UK and Isle of Man, straight from us to you.",
  },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 140]);
  const markY = useTransform(scrollY, [0, 600], [0, -60]);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]));
  }, []);

  const featured = [...products.filter((p) => p.featured), ...products.filter((p) => !p.featured)].slice(0, 4);

  return (
    <div>
      {/* HERO */}
      <section className="relative flex min-h-screen items-center overflow-hidden">
        <motion.div style={{ y: heroY }} className="absolute inset-0 -bottom-40">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FAF8F4] via-[#FAF8F4]/80 to-[#FAF8F4]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FAF8F4] via-transparent to-[#FAF8F4]/70" />
        </motion.div>

        <motion.div
          style={{ y: markY }}
          className="pointer-events-none absolute -right-24 top-1/2 hidden -translate-y-1/2 opacity-70 md:block lg:right-8"
        >
          <TriskelionMark size={480} spin armColor="#F5C518" hubColor="#E52E39" className="opacity-40" />
        </motion.div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-28 pt-32 sm:px-8 lg:px-16">
          <HeroLine delay={0.1}>
            <span className="mb-6 inline-flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-[#E52E39]">
              <span className="h-px w-10 bg-[#E52E39]" />
              Est. Ellan Vannin — 54.2361° N, 4.5481° W
            </span>
          </HeroLine>
          <h1 className="font-display text-6xl font-black uppercase leading-[0.92] tracking-tight text-stone-950 sm:text-7xl lg:text-8xl">
            <HeroLine delay={0.25}>Built on the</HeroLine>
            <HeroLine delay={0.37}>
              Island.{" "}
              <span className="font-serif normal-case italic tracking-normal text-[#C79500]">Worn everywhere.</span>
            </HeroLine>
          </h1>
          <HeroLine delay={0.55} className="mt-8 max-w-xl">
            <span className="block text-base leading-relaxed text-stone-700 sm:text-lg">
              V.I.A is independent apparel from the Isle of Man — heritage streetwear with TT racing blood. Every order
              is personally handled by us, with honest delivery times and no dead stock.
            </span>
          </HeroLine>
          <HeroLine delay={0.7} className="mt-10">
            <span className="flex flex-wrap items-center gap-4">
              <Link
                data-testid={T.heroExplore}
                to="/shop"
                className="group inline-flex items-center gap-3 bg-[#E52E39] px-8 py-4 font-display text-lg font-black uppercase tracking-wide text-white transition-all hover:bg-[#c9202a] hover:shadow-[0_0_40px_rgba(229,46,57,0.4)]"
              >
                Shop the drop
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/shop"
                className="inline-flex items-center border border-stone-400 px-8 py-4 font-display text-lg font-black uppercase tracking-wide text-stone-950 transition-colors hover:border-[#F5C518] hover:text-[#C79500]"
              >
                Browse all
              </Link>
            </span>
          </HeroLine>
        </div>

        <div className="absolute inset-x-0 bottom-0 z-10 border-t border-stone-200 bg-[#FAF8F4]/60 backdrop-blur-md">
          <div className="mx-auto grid max-w-7xl grid-cols-3 divide-x divide-stone-200 px-4 sm:px-8 lg:px-16">
            {[
              ["100%", "Independent"],
              ["Personally handled", "Every order"],
              ["£ GBP", "Secure card payment"],
            ].map(([a, b]) => (
              <div key={b} className="px-3 py-4 sm:px-6">
                <p className="font-display text-lg font-bold uppercase tracking-wide text-stone-950 sm:text-xl">{a}</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-stone-500 sm:text-[10px]">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* FEATURED DROP */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="via-label">The current drop</p>
              <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-stone-950 sm:text-4xl lg:text-5xl">
                Fresh off the Island
              </h2>
            </div>
            <Link
              to="/shop"
              className="group inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#C79500] hover:text-stone-950"
            >
              View everything
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
          {featured.length === 0 ? (
            <p className="py-12 text-center font-mono text-sm uppercase tracking-widest text-stone-400">
              The drop is being prepared — check back shortly.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12">
              {featured.map((p, i) => (
                <Reveal
                  key={p.id}
                  delay={i * 0.08}
                  className={i === 0 ? "lg:col-span-7" : i === 1 ? "lg:col-span-5" : i === 2 ? "lg:col-span-4" : "lg:col-span-8"}
                >
                  <ProductCard product={p} className="h-full" />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* HERITAGE */}
      <section id="heritage" className="border-y border-stone-200 bg-[#FFFBEF] py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-8 lg:grid-cols-2 lg:px-16">
          <Reveal>
            <div className="relative">
              <div className="absolute -left-3 -top-3 h-full w-full border border-[#E52E39]/60" />
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={HERITAGE_IMG} alt="The Isle of Man coastline" className="h-full w-full object-cover" />
              </div>
              <div className="absolute -bottom-5 left-6 bg-[#E52E39] px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-white">
                Ellan Vannin — the Isle of Man
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="via-label">Heritage</p>
            <h2 className="font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-stone-950 sm:text-4xl lg:text-5xl">
              Three legs, one island, <span className="font-serif normal-case italic text-[#C79500]">zero shortcuts</span>
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-stone-700">
              <p>
                Vannin is the Manx name for our home — Ellan Vannin, the Isle of Man. A island of sea cliffs, mountain
                mist and the greatest road race on earth. Our mark is the Three Legs of Man, and our attitude comes
                straight off the TT course: built for weather, built to last.
              </p>
              <p>
                We're independent in the truest sense. When you order, we handle it personally —
                checking the piece, packing it and posting it to you. It's more personal than a faceless mega-store,
                and that's exactly the point.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-3 divide-x divide-stone-200 border-y border-stone-200">
              {[
                ["54.2°N", "Douglas Bay"],
                ["37.7mi", "TT Course"],
                ["1", "Island"],
              ].map(([a, b]) => (
                <div key={b} className="px-4 py-5">
                  <p className="font-display text-2xl font-black uppercase text-[#C79500]">{a}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">{b}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal className="mb-12">
            <p className="via-label">How it works</p>
            <h2 className="max-w-2xl font-display text-3xl font-extrabold uppercase tracking-tight text-stone-950 sm:text-4xl lg:text-5xl">
              Ordered by you. Fulfilled by hand.
            </h2>
          </Reveal>
          <div className="grid gap-4 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div className="group relative h-full border border-stone-200 bg-[#FFFFFF] p-8 transition-colors hover:border-[#E52E39]/50">
                  <span className="absolute right-6 top-6 font-display text-5xl font-black text-stone-900/5 transition-colors group-hover:text-[#E52E39]/15">
                    0{i + 1}
                  </span>
                  <s.icon className="h-8 w-8 text-[#C79500]" />
                  <h3 className="mt-6 font-display text-2xl font-bold uppercase tracking-wide text-stone-950">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone-500">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone-200 bg-[#FFFBEF] py-16 md:py-24">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-4 sm:px-8 lg:flex-row lg:items-center lg:justify-between lg:px-16">
          <Reveal>
            <h2 className="max-w-xl font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-stone-950 sm:text-4xl">
              Wear where you're from. <span className="font-serif normal-case italic text-[#C79500]">Even if it's far from here.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/shop"
              className="group inline-flex items-center gap-3 bg-[#E52E39] px-10 py-5 font-display text-xl font-black uppercase tracking-wide text-white transition-all hover:bg-[#c9202a] hover:shadow-[0_0_40px_rgba(229,46,57,0.4)]"
            >
              Shop V.I.A
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
